import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserLearningPaths } from '../services/firebase';
import { loadCardsProgress, getDueCards, getDeckStats, loadMasterDeck, getDeckLevels } from '../services/spacedRepetition';
import './FlashcardTopics.css';

function FlashcardTopics() {
    const [learningPaths, setLearningPaths] = useState([]);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadLearningPaths();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLearningPaths = async () => {
        setLoading(true);
        try {
            const result = await getUserLearningPaths(currentUser.uid);
            if (result.success) {
                // Enhance each path with flashcard stats
                const pathsWithStats = result.data.map(path => {
                    // Try new deck-based storage first
                    const deckLevels = getDeckLevels(currentUser.uid, path.topic);
                    let allCards = [];

                    if (deckLevels.length > 0) {
                        // Use new deck system
                        allCards = loadMasterDeck(currentUser.uid, path.topic);
                    } else {
                        // Fall back to old storage
                        allCards = loadCardsProgress(currentUser.uid, path.topic) || [];
                    }

                    let stats = {
                        total: 0,
                        due: 0,
                        new: 0,
                        learning: 0,
                        mature: 0,
                        deckCount: deckLevels.length
                    };

                    if (allCards.length > 0) {
                        const deckStats = getDeckStats(allCards);
                        const dueCards = getDueCards(allCards);
                        stats = {
                            total: allCards.length,
                            due: dueCards.length,
                            new: deckStats.new,
                            learning: deckStats.learning,
                            mature: deckStats.mature,
                            deckCount: deckLevels.length
                        };
                    }

                    return {
                        ...path,
                        flashcardStats: stats
                    };
                });

                setLearningPaths(pathsWithStats);
            }
        } catch (error) {
            console.error('Error loading learning paths:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTopicClick = (path) => {
        // Navigate to the new deck-based view
        navigate('/flashcard-decks', {
            state: {
                topic: path.topic
            }
        });
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <p>Loading your topics...</p>
            </div>
        );
    }

    if (learningPaths.length === 0) {
        return (
            <div className="flashcard-topics-container">
                <h1>Flashcards</h1>
                <div className="empty-state">
                    <div className="empty-icon">📇</div>
                    <h2>No Learning Paths Yet</h2>
                    <p>Start a learning path from the home page to generate flashcards!</p>
                    <button className="btn btn-primary" onClick={() => navigate('/home')}>
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flashcard-topics-container">
            <div className="header-section">
                <h1>📇 Study Flashcards</h1>
                <p className="subtitle">Select a topic to review your flashcards</p>
            </div>

            <div className="topics-grid">
                {learningPaths.map((path) => (
                    <div
                        key={path.id}
                        className="topic-card"
                        onClick={() => handleTopicClick(path)}
                    >
                        <div className="topic-header">
                            <h3>{path.topic}</h3>
                            {path.flashcardStats.due > 0 && (
                                <span className="due-badge">{path.flashcardStats.due} due</span>
                            )}
                        </div>

                        <div className="topic-stats">
                            {path.flashcardStats.total > 0 ? (
                                <>
                                    <div className="stat-item">
                                        <span className="stat-label">Total Cards</span>
                                        <span className="stat-value">{path.flashcardStats.total}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">New</span>
                                        <span className="stat-value stat-new">{path.flashcardStats.new}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Learning</span>
                                        <span className="stat-value stat-learning">{path.flashcardStats.learning}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Mastered</span>
                                        <span className="stat-value stat-mature">{path.flashcardStats.mature}</span>
                                    </div>
                                </>
                            ) : (
                                <div className="no-cards-message">
                                    <span>No flashcards yet - click to generate!</span>
                                </div>
                            )}
                        </div>

                        <div className="topic-footer">
                            <span className="topic-modules">
                                {path.flashcardStats.deckCount > 0
                                    ? `${path.flashcardStats.deckCount} decks`
                                    : `${path.modules?.length || 0} levels`}
                            </span>
                            <button className="btn btn-primary btn-small">
                                {path.flashcardStats.total > 0 ? 'View Decks →' : 'Start Learning →'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default FlashcardTopics;
